
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Mic, X, BookText, CheckCircle, Loader2, Square, Headphones, Languages, ArrowUp, HelpCircle, Copy } from "lucide-react";
import SearchInput from "@/components/clients/SearchClientInput";
import { apiService } from "@/api/api";
import { Client } from "@/types/client";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SOAPTemplate from "@/components/soap/SOAPTemplate";
import useTranscribeMic from "@/hooks/use-transcribe";
import { useDictationProcessor } from "@/hooks/use-dictation-processor";
import { getTemplateId } from "@/utils/template-mapping";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import MultiSearchInput from "@/components/diagnosis/MultiSearchInput";
import TextareaAutosize from 'react-textarea-autosize';
import { useIsMobile } from "@/hooks/use-mobile";

// 1. Import your JSON data
import diagnosisCodesData from "@/data/diagnosisCodes.json"; // Adjust path as needed

interface DiagnosisCode {
  code: string;
  description: string;
}

const DictationInterface = () => {
  // --- STATE MANAGEMENT ---
  const [dictationPhase, setDictationPhase] = useState<'general' | 'refine'>('general');
  const [generalNotes, setGeneralNotes] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [dictationTitle, setDictationTitle] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("soap");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [templateData, setTemplateData] = useState<any>({});
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [searchInputKey, setSearchInputKey] = useState(0);
  const [encounterSettingsOpen, setEncounterSettingsOpen] = useState(false);
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("Auto-detect");
  const [micPermission, setMicPermission] = useState(true);
  const [mics, setMics] = useState<any[]>([]);

  // State to hold the formatted diagnosis suggestions
  const [diagnosisSuggestions, setDiagnosisSuggestions] = useState<string[]>([]);

  // --- HOOKS ---
  const { toast } = useToast();
  const navigate = useNavigate();
  const titleInputRef = useRef<HTMLInputElement>(null);
  const processedTranscriptLength = useRef(0);
  const { isTranscribing, currentTranscript, transcribedText, startTranscription, stopTranscription, clearTranscribedText } = useTranscribeMic({
    selectedLanguage,
    selectedMic
  });
  const { isProcessing, processingStep, startProcessingSession } = useDictationProcessor();
  const isMobile = useIsMobile();

  // --- Data Fetching and Initialization ---
  useEffect(() => {
    apiService.getClients()
      .then(setClients)
      .catch(err => {
        console.error('❌ Error fetching clients:', err);
        toast({ title: "Error", description: "Failed to fetch client list.", variant: "destructive" });
      })
      .finally(() => setClientsLoading(false));

    // Process the imported JSON data into the desired string format
    const formattedSuggestions = (diagnosisCodesData as DiagnosisCode[]).map(
      (item: DiagnosisCode) => `${item.code} - ${item.description}`
    );
    setDiagnosisSuggestions(formattedSuggestions);

  }, [toast]);

  useEffect(() => {
  // Process new transcript segments
  if (transcribedText.length > processedTranscriptLength.current) {
    const newSegments = transcribedText.slice(processedTranscriptLength.current);
    const newContent = newSegments.map(t => t.content).join(' ').trim();
    
    if (newContent) {
      setGeneralNotes(prev => {
        const updatedNotes = prev ? `${prev} ${newContent}`.trim() : newContent;
        console.log('Adding new transcript content:', newContent);
        console.log('Updated notes:', updatedNotes);
        return updatedNotes;
      });
    }
    
    // Update the processed length
    processedTranscriptLength.current = transcribedText.length;
    console.log('Processed transcript length updated to:', processedTranscriptLength.current);
  }
}, [transcribedText]);

  useEffect(() => { navigator.mediaDevices.enumerateDevices().then(devices => { const audioInputs = devices.filter(device => device.kind === 'audioinput'); setMics(audioInputs); if (audioInputs.length > 0 && !selectedMic) { setSelectedMic(audioInputs[0].deviceId); } }).catch(err => console.error('Error getting audio devices:', err)); }, []);
  useEffect(() => { navigator.permissions.query({ name: 'microphone' as PermissionName }).then(permission => { setMicPermission(permission.state === 'granted'); }).catch(() => setMicPermission(false)); }, []);

  const handleGeneralMicToggle = useCallback(() => {
  if (!dictationTitle.trim() || !selectedClient) {
    toast({
      title: "Missing Information",
      description: "Please enter a session title and select a patient before recording.",
      variant: "destructive"
    });
    return;
  }

  isTranscribing
    ? stopTranscription()
    : startTranscription().catch(err => {
        console.error('Failed to start transcription:', err);
        toast({
          title: "Recording Error",
          description: "Could not start voice recording.",
          variant: "destructive"
        });
      });
}, [isTranscribing, startTranscription, stopTranscription, toast, dictationTitle, selectedClient]);

  const handleTemplateDataChange = useCallback((updatedData: any) => { setTemplateData(prevData => ({ ...prevData, ...updatedData, })); }, []);

  const populateTemplateFields = useCallback((enhancedResponse: any) => {
    if (enhancedResponse && enhancedResponse.sections) {
      const rawSections = enhancedResponse.sections;

      const flattenedData = Object.keys(rawSections).reduce((acc, key) => {
        acc[key] = rawSections[key]?.content || "";
        return acc;
      }, {} as Record<string, string>);

      setTemplateData(flattenedData);

      const codesString = flattenedData.diagnosis || "";
      setSelectedCodes(codesString.split(',').map(s => s.trim()).filter(Boolean));
    }
  }, []);

  const handleProcessNotes = useCallback(async () => {
    if (isTranscribing) await stopTranscription();
    if (!selectedClient?.client_id || !selectedTemplate || !generalNotes.trim() || !dictationTitle.trim()) {
      toast({ title: "Missing Information", description: "A client, template, title, and dictation notes are all required.", variant: "destructive" });
      return;
    }
    const result = await startProcessingSession({
      template_type: getTemplateId(selectedTemplate),
      dictation_notes: generalNotes.trim(),
      client_id: selectedClient.client_id,
      session_title: dictationTitle.trim()
    });

    if (result.success && result.enhanced_sections && result.session_id) {
      populateTemplateFields({ sections: result.enhanced_sections });
      setSessionId(result.session_id);
      setDictationPhase('refine');
    } else {
      console.error("Processing failed:", result.error || "Missing session_id in response");
      toast({ title: "Processing Failed", description: "Could not process the notes. Please try again.", variant: "destructive" });
    }
  }, [isTranscribing, stopTranscription, selectedClient, selectedTemplate, generalNotes, dictationTitle, toast, startProcessingSession, populateTemplateFields]);

  const approveAndSaveNote = useCallback(async () => {
    if (!sessionId) {
      toast({
        title: "Save Error",
        description: "A session ID was not found. Cannot save the note.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        enhanced_sections: templateData,
        dictation_notes: generalNotes.trim(),
        diagnosisCodesData: selectedCodes.join(', '),
        session_status: "completed",
        session_type: "dictation",

      };

      console.log(`Updating session ${sessionId} with data:`, payload);
      await apiService.updateSessionData(sessionId, payload);

      toast({
        title: "Note Saved!",
        description: "The session note has been updated successfully.",
      });

      navigate('/');

    } catch (error) {
      console.error("Failed to update session note:", error);
      toast({
        title: "Update Failed",
        description: "There was an error saving your note. Please try again.",
        variant: "destructive",
      });
    }
  }, [sessionId, templateData, generalNotes, toast, navigate]);

  const handleCopyNote = useCallback(() => { const allContent = [ templateData.subjective && `Subjective: ${templateData.subjective}`, templateData.objective && `Objective: ${templateData.objective}`, templateData.assessment && `Assessment: ${templateData.assessment}`, templateData.plan && `Plan: ${templateData.plan}`, templateData.diagnosis && `Diagnosis Codes: ${templateData.diagnosis}` ].filter(Boolean).join('\n\n'); if (allContent.trim()) { navigator.clipboard.writeText(allContent).then( () => toast({ title: "Copied!", description: "The final note has been copied to your clipboard." }), () => toast({ title: "Copy Failed", variant: "destructive", description: "Could not copy the note." }) ); } else { toast({ title: "No Content", description: "There is nothing to copy.", variant: "destructive" }); } }, [templateData, toast]);
  const handleTitleClick = () => setIsEditingTitle(true);
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => setDictationTitle(e.target.value);
  const handleTitleBlur = () => setIsEditingTitle(false);
  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') setIsEditingTitle(false); };
  const handleClientSelect = (client: Client | null) => {
    console.log("Client selected:", client);
    setSelectedClient(client);
  };
  const handleClearClient = () => { setSelectedClient(null); setSearchInputKey(Date.now()); };
  const handleTemplateSelect = (value: string) => {
  setSelectedTemplate(value);
  setTemplateData({});
  setDictationPhase('general');
  setGeneralNotes('');
  
  // FIX: Reset the transcript processing when changing templates
  processedTranscriptLength.current = 0;
  
  // Clear existing transcripts if needed
  // clearTranscribedText(); // Uncomment if you want to clear on template change
  
  if (!dictationTitle) {
    setDictationTitle(`${value.toUpperCase()} Session`);
  }
};
  const handleClearGeneralNotes = () => {
  setGeneralNotes("");
  // FIX: Reset transcript processing when clearing notes
  processedTranscriptLength.current = 0;
  clearTranscribedText(); // Clear the transcript history as well
};
  const handleCodesChange = (codes: string[]) => { setSelectedCodes(codes); setTemplateData(prev => ({...prev, diagnosis: codes.join(', ')})); };

  const templates = ["soap", " "];
  const isGeneralPhase = dictationPhase === 'general';
  const isProcessButtonEnabled = !isProcessing && !!generalNotes.trim();

  return (
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6 w-full h-full flex flex-col overflow-hidden relative">
      {isProcessing && ( 
        <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-50"> 
          <Loader2 className="h-8 w-8 sm:h-12 sm:w-12 animate-spin text-blue-600" /> 
          <p className="mt-4 text-sm sm:text-lg text-gray-700 font-medium text-center px-4">{processingStep || 'Processing your notes...'}</p> 
        </div> 
      )}

      <div className="flex flex-col space-y-4 sm:space-y-8 flex-shrink-0">
        <div className="text-xs sm:text-sm text-gray-500">{format(new Date(), isMobile ? "MMM d, yyyy" : "EEEE, MMMM d, yyyy")}</div>
        
        {/* Mobile-first responsive header */}
        <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-start sm:justify-between">
            <div className="w-full sm:w-2/5">
                {isEditingTitle ? (
                <input 
                  ref={titleInputRef} 
                  value={dictationTitle} 
                  onChange={handleTitleChange} 
                  onBlur={handleTitleBlur} 
                  onKeyDown={handleTitleKeyDown} 
                  className="w-full text-lg sm:text-3xl font-light text-gray-600 placeholder:text-gray-400 bg-transparent border-0 border-b border-gray-300 focus:border-gray-500 outline-none px-0 py-2 transition-colors" 
                  placeholder="Session title" 
                  autoFocus 
                />
                ) : (
                <div 
                  className="w-full text-lg sm:text-3xl font-light text-gray-400 px-0 py-2 cursor-pointer hover:text-gray-600 transition-colors border-b border-gray-300" 
                  onClick={handleTitleClick}
                >
                    {dictationTitle || "Add session title"}
                </div>
                )}
            </div>
            <div className="w-full sm:w-2/5 relative">
                <SearchInput 
                  key={searchInputKey} 
                  items={clients} 
                  onSelect={handleClientSelect} 
                  isLoading={clientsLoading} 
                  placeholder="Search client name..." 
                  getDisplayValue={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim()} 
                  getItemId={(c) => c.client_id} 
                  renderItem={(c: Client) => (
                    <>
                      <div className="font-medium">{c.first_name} {c.last_name}</div>
                      <div className="text-xs text-gray-500">{c.primary_diagnosis || "No diagnosis"}</div>
                    </>
                  )} 
                  filterFunction={(c, t) => `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(t.toLowerCase())} 
                  hideClearButton={!!selectedClient} 
                />
                {selectedClient && (
                  <button 
                    type="button" 
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1 z-20 mobile-touch-target" 
                    onClick={handleClearClient}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
            </div>
        </div>

        {/* Mobile-optimized settings section */}
        <div className="w-full flex flex-col items-center">
          <Collapsible open={encounterSettingsOpen} onOpenChange={setEncounterSettingsOpen} className="w-full">
            <CollapsibleTrigger className="flex items-center justify-center mb-4 text-gray-600 w-full mobile-touch-target">
              <div className="flex items-center">
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  className={`w-5 h-5 mr-2 text-gray-500 transition-transform ${encounterSettingsOpen ? 'rotate-180' : ''}`}
                >
                  <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-gray-600 text-sm sm:text-base">Dictation Settings</span>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3 sm:gap-4 mt-4 w-full">
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger className="bg-gray-100 rounded-full px-4 py-2 flex items-center gap-2 border-none w-full sm:w-[200px] mobile-touch-target">
                    <BookText className="w-5 h-5 text-gray-700" />
                    <span className="text-sm">Template:</span>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(template => ( 
                      <SelectItem key={template} value={template} className="capitalize">{template}</SelectItem> 
                    ))}
                  </SelectContent>
                </Select>
                
                {micPermission === true ? (
                  <Select value={selectedMic || ""} onValueChange={setSelectedMic}>
                    <SelectTrigger className="bg-gray-100 rounded-full px-4 py-2 flex items-center gap-2 border-none w-full sm:w-[200px] mobile-touch-target">
                      <Headphones className="w-5 h-5 text-gray-700" />
                      <SelectValue placeholder="Select microphone" />
                    </SelectTrigger>
                    <SelectContent>
                      {mics.map((mic, index) => ( 
                        <SelectItem key={mic.deviceId || index} value={mic.deviceId || index.toString()}>
                          {mic.label || `Microphone ${index + 1}`}
                        </SelectItem> 
                      ))}
                    </SelectContent>
                  </Select>
                ) : ( 
                  <div className="bg-yellow-100 text-yellow-800 rounded-full px-4 py-2 flex items-center gap-2 w-full sm:w-[200px] text-sm mobile-touch-target">
                    <Headphones className="w-5 h-5" />
                    <span>Allow mic access</span>
                  </div> 
                )}
                
                <Select defaultValue={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger className="bg-gray-100 rounded-full px-4 py-2 flex items-center gap-2 border-none w-full sm:w-[200px] mobile-touch-target">
                    <Languages className="w-5 h-5 text-gray-700" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Auto-detect">Auto-detect</SelectItem>
                    <SelectItem value="English">English</SelectItem>
                    <SelectItem value="Arabic">Arabic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
      
      {/* Mobile-optimized content area */}
      <div className="flex-1 overflow-y-auto mb-4 mt-4 sm:mt-0">
        {selectedTemplate === "soap" && (
            <SOAPTemplate
              data={templateData}
              onDataChange={handleTemplateDataChange}
              dictationPhase={dictationPhase} 
            />
        )}
        
        <div className="mt-4">
          <MultiSearchInput
            selectedItems={selectedCodes}
            onChange={handleCodesChange}
            searchSuggestions={diagnosisSuggestions}
            placeholder="Search and add diagnosis codes..."
          />
        </div>
      </div>

      {/* Mobile-optimized footer controls */}
      <div className="bg-white border-t border-gray-200 pt-4 mt-auto flex-shrink-0 mobile-safe-area">
        <div className="w-full flex flex-col items-center">
            {isGeneralPhase ? (
                <>
                <Button 
                  onClick={handleGeneralMicToggle} 
                  className={`h-12 w-12 sm:h-16 sm:w-16 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center mb-4 mobile-touch-target ${ 
                    isTranscribing ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-800 hover:bg-gray-900' 
                  }`} 
                  disabled={isProcessing || !dictationTitle.trim() || !selectedClient}
                >
                    {isTranscribing ? (
                      <Square className="h-5 w-5 sm:h-7 sm:w-7 text-white fill-white" />
                    ) : (
                      <Mic className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    )}
                </Button>
                
                <div className="w-full relative">
                    {!!generalNotes && ( 
                      <Button 
                        onClick={handleClearGeneralNotes} 
                        variant="ghost" 
                        className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full z-10 text-gray-400 hover:text-gray-700 hover:bg-gray-100 mobile-touch-target"
                      >
                        <X className="h-5 w-5" />
                      </Button> 
                    )}
                    
                    <TextareaAutosize 
                      className="w-full resize-none border-2 rounded-lg py-3 transition-all duration-200 bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[52px] text-sm sm:text-base pl-12 pr-20 sm:pr-28" 
                      placeholder="Dictate or type what you want PsyBytes to add under each heading..." 
                      value={generalNotes + (currentTranscript ? (generalNotes ? ' ' : '') + currentTranscript : '')} 
                      onChange={(e) => setGeneralNotes(e.target.value)} 
                      disabled={isProcessing} 
                    />
                    
                    <Button 
                      variant="ghost" 
                      className="absolute right-12 sm:right-14 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full z-10 text-gray-400 hover:text-gray-700 hover:bg-gray-100 mobile-touch-target"
                    >
                      <HelpCircle className="h-5 w-5" />
                    </Button>
                    
                    <Button 
                      onClick={handleProcessNotes} 
                      disabled={!isProcessButtonEnabled} 
                      className={`absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 rounded-full transition-colors duration-200 flex items-center justify-center mobile-touch-target ${ 
                        isProcessButtonEnabled ? 'bg-gray-800 text-white hover:bg-gray-900' : 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                      }`}
                    >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                    ) : (
                      <ArrowUp className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                    </Button>
                </div>
                </>
            ) : (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full h-auto sm:h-[108px] py-4 sm:py-0">
                <Button 
                  onClick={handleCopyNote} 
                  variant="outline" 
                  size={isMobile ? "default" : "lg"} 
                  className="h-10 sm:h-12 w-full sm:w-auto mobile-touch-target"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy note
                </Button>
                <Button 
                  onClick={approveAndSaveNote} 
                  size={isMobile ? "default" : "lg"} 
                  className="bg-green-600 hover:bg-green-700 h-10 sm:h-12 w-full sm:w-auto mobile-touch-target"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Approve & Save Note
                </Button>
                </div>
            )}
        </div>
      </div>
      </div>
  );
};

export default DictationInterface;
